import { ColorButton } from '../UI';
import { useNewStrategyContext } from './NewStrategyContext';
import { ProfitLoss } from '../ProfitLoss';
import { Sort } from './Sort';

import { COLUMNS_DUPLICATE_STRATEGY } from '../../constants/lists';

import { useSort } from '../../hooks/useSort';

import {
  aggregateGroups,
  getSymbol,
  getTotalValues,
} from '../../helpers/functions';
import { useUserStrategies } from '../../context/UserStrategies';

import { StrategyDuplication as StrategyDuplicationType } from '../NewStrategy/types';
import { Currency } from '../../types/common';
import { IStrategyAsset, TTickerCurrency } from '../../types/model';
import { StrategyDuplication } from './StrategyDuplication';
import moment from 'moment';

export const Column = ({
  element,
  sort,
  span,
  className = `col-span-${span}`,
}: {
  className?: string;
  element: string | JSX.Element;
  sort?: JSX.Element;
  span: number;
}) => {
  return (
    <div className={className}>
      <div
        className={`
        flex
        items-center
        flex-shrink-0
        ${span === 2 && 'pl-5'}
        text-sm
        font-medium
      `}
      >
        {sort}
        {element}
      </div>
    </div>
  );
};

export const StrategyTable = ({
  tableName,
  isNewStrategy = false,
}: {
  tableName: string;
  isNewStrategy?: boolean;
}) => {
  const { strategies } = useUserStrategies();
  const { newStrategy } = useNewStrategyContext();
  const { type, getSortValue, handleSort } = useSort([
    ...COLUMNS_DUPLICATE_STRATEGY,
  ]);

  const strategiesList: StrategyDuplicationType['strategy'][] = isNewStrategy
    ? [
        {
          assets: newStrategy.assets,
          currency: newStrategy.reportingCurrency.code,
          id: newStrategy.strategyName,
          name: newStrategy.strategyName,
          ...getCategoriesAndTotal(
            newStrategy.assets,
            newStrategy.reportingCurrency.code.toLocaleLowerCase() as TTickerCurrency,
            type,
            getSortValue
          ),
        },
      ]
    : strategies.map(
        ({ strategyAssets: { items: assets }, currency, id, name }) => ({
          assets,
          currency,
          id,
          name,
          ...getCategoriesAndTotal(
            assets,
            currency.toLocaleLowerCase() as TTickerCurrency,
            type,
            getSortValue
          ),
        })
      );

  return (
    <div
      className={`
        h-full
        w-full
        flex
        flex-col
        border-l
        border-grey-30
        dark:border-grey-70
        relative
      `}
    >
      <div className="absolute flex flex-col top-0 bottom-0 w-full">
        <div className="flex flex-col flex-grow min-h-0">
          {/* header */}
          <div
            className={`
              flex
              w-full
              items-center
              font-medium
              pl-5
              min-h-16
              border-b
              border-grey-30
              dark:border-grey-70
            `}
          >
            <span className={'uppercase'}>{tableName}</span>
            {isNewStrategy && (
              <div className={'inline-flex ml-3 items-center'}>
                <ColorButton
                  radius={3}
                  color={newStrategy.strategyColor!}
                  isReadOnly={true}
                />
                <div className="pl-2 font-bold">{newStrategy.strategyName}</div>
              </div>
            )}
          </div>

          {/* columns */}
          <div
            className={`
              grid
              grid-cols-6
              items-center
              border-b
              border-grey-30
              dark:border-grey-70
              py-3
            `}
          >
            {COLUMNS_DUPLICATE_STRATEGY.map((columnName, i) => (
              <Column
                key={columnName}
                element={
                  <Sort
                    className={`
                      flex
                      items-center
                      text-sm
                      font-medium
                    `}
                    columnName={columnName}
                    type={type}
                    handleSort={handleSort}
                  />
                }
                span={i ? 1 : 2}
              />
            ))}
          </div>

          {/* data */}
          <div
            className={`flex-grow overflow-y-auto ${isNewStrategy && 'mb-16'}`}
          >
            {strategiesList.map((strategy) => (
              <StrategyDuplication
                isNewStrategy={isNewStrategy}
                strategy={strategy}
                key={strategy.id}
              />
            ))}
          </div>

          {/* total */}
          {isNewStrategy && (
            <div
              className={`
              absolute
              bottom-0
              grid
              grid-cols-6
              min-h-16
              w-full
              items-center
              border-t
              border-grey-30
              dark:border-grey-70
              bg-lime
              pl-5
              dark:text-black
            `}
            >
              {[
                'Total',
                '',
                '',
                <ProfitLoss
                  center
                  percentage={strategiesList[0].percent}
                  symbol={getSymbol(strategiesList[0].currency)}
                  value={strategiesList[0].totalValue}
                  className={'dark:text-black'}
                />,
                `100%`,
              ].map((element, i) => (
                <Column
                  key={'bottom_cols_' + i}
                  element={element}
                  span={i ? 1 : 2}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getCategoriesAndTotal = (
  assets: IStrategyAsset[],
  currency: TTickerCurrency,
  type: string,
  getSortValue: (a: string | number, b: string | number) => number
) => {
  // const currency = CURRENCIES.find(
  //   ({ symbol }) => symbol === currencySymbol
  // )!.code.toLocaleLowerCase() as 'eur' | 'usd';

  const categories = aggregateGroups({
    assets,
    currency: currency.toLocaleUpperCase() as Currency['code'],
  }).sort((a, b) => getSortValue(a.title, b.title));

  const { percent, totalValue } = getTotalValues(
    assets,
    currency,
    moment().format('YYYY-MM-DD')
  );

  if (type === 'ASSET') {
    categories.forEach(({ assets }) =>
      assets.sort((a, b) => getSortValue(a.name, b.name))
    );
  } else if (type === 'QUANTITY') {
    categories.forEach(({ assets }) =>
      assets.sort((a, b) => getSortValue(a.unit, b.unit))
    );
  } else if (type === 'LAST PRICE') {
    categories.forEach(({ assets }) =>
      assets.sort((a, b) => {
        const priceA = a.asset.history[0]?.[currency] ?? 0;
        const priceB = b.asset.history[0]?.[currency] ?? 0;

        return getSortValue(priceA, priceB);
      })
    );
  } else if (type === 'TOT VALUE' || type === 'ALLOCATION') {
    categories.sort((a, b) => {
      const assetsA = a.assets;
      const assetsB = b.assets;

      const { totalValue: sumA } = getTotalValues(
        assetsA,
        currency,
        moment().format('YYYY-MM-DD')
      );
      const { totalValue: sumB } = getTotalValues(
        assetsB,
        currency,
        moment().format('YYYY-MM-DD')
      );

      return getSortValue(sumA, sumB);
    });

    categories.forEach(({ assets }) =>
      assets.sort((a, b) => {
        const valueA = a.unit * (a.asset.history[0]?.[currency] ?? 0);
        const valueB = b.unit * (a.asset.history[0]?.[currency] ?? 0);

        return getSortValue(valueA, valueB);
      })
    );
  }

  return { categories, percent, totalValue };
};
